import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, ExternalLink, Trash2, ChevronDown, ChevronUp, Link as LinkIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useTenancy } from '@/contexts/TenancyContext';
import { toast } from 'sonner';
import { getDocument } from '@/services/documentsService';
import DocumentPage from '@/app/pages/document-detail';
import { renderDate } from '@shared/renderDate';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DocumentsCombobox, DocumentsProvider } from '@/components/ui/DocumentsCombobox';

interface LinkedDocument {
  docID: string;
  fileName: string;
  displayName?: string;
  documentType?: string;
  nextReviewDate?: Date | string;
  lastReviewDate?: Date | string;
}

interface DialogDocumentDetailProps {
  prefillData: any;
  onSave: (doc: any) => void;
  onClose: () => void;
}

const DialogDocumentDetail: React.FC<DialogDocumentDetailProps> = ({ prefillData, onSave, onClose }) => {
  const [document, setDocument] = useState<any>(null);
  const [redirected, setRedirected] = useState(false);

  const isEditMode = prefillData?.documentID || prefillData?.isEdit;

  useEffect(() => {
    if (prefillData) {
      if (prefillData.documentID || prefillData.isEdit) {
        setDocument({
          ...prefillData,
        });
      } else {
        setDocument({
          ...prefillData,
          documentType: prefillData.documentType || 'Audit',
          status: prefillData.status || 'Draft',
          dateAdded: prefillData.dateAdded || new Date().toISOString(),
        });
      }
    }
  }, [prefillData]);

  useEffect(() => {
    if (!isEditMode && document && !redirected) {
      setRedirected(true);
      sessionStorage.setItem('documentPrefillData', JSON.stringify(document));
      window.open('/documentdetail/new', '_blank');
      onClose();
    }
  }, [document, isEditMode, onClose, redirected]);

  const handleSave = (savedDoc: any) => {
    onSave(savedDoc);
  };

  if (!document) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (isEditMode) {
    return (
      <DocumentPage
        documentID={prefillData.documentID}
        prefillData={document}
        onSave={handleSave}
        onCancel={onClose}
        onBack={onClose}
        referer="Audit Centre"
      />
    );
  }

  return (
    <div className="flex items-center justify-center h-full">
      <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      <span className="ml-2 text-sm text-muted-foreground">Opening document editor...</span>
    </div>
  );
};

interface AuditLinkedDocumentsProps {
  auditId: string;
  auditScope?: string;
  defaultDocumentType?: string;
  linkedDocuments?: string[];
  onDocumentsChange?: (docIds: string[]) => Promise<void> | void;
  isReadOnly?: boolean;
}

export const AuditLinkedDocuments: React.FC<AuditLinkedDocumentsProps> = ({
  auditId,
  auditScope,
  defaultDocumentType = 'Audit',
  linkedDocuments = [],
  onDocumentsChange,
  isReadOnly = false,
}) => {
  const { activeTenancy } = useTenancy();
  const [documents, setDocuments] = useState<LinkedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [prefillData, setPrefillData] = useState<any>(null);
  const [dialogKey, setDialogKey] = useState(0);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);
  const [docToUnlink, setDocToUnlink] = useState<string | null>(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedDocsToLink, setSelectedDocsToLink] = useState<string[]>([]);

  const fetchDocuments = useCallback(async (docIdsToFetch?: string[]) => {
    const idsToFetch = docIdsToFetch || linkedDocuments;

    if (!activeTenancy?.id || idsToFetch.length === 0) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const fetchedDocs: LinkedDocument[] = [];

      for (const docId of idsToFetch) {
        try {
          const docData = await getDocument(docId, activeTenancy.id);
          if (docData) {
            fetchedDocs.push({
              docID: docId,
              fileName: docData.fileName || (docData as any).title || 'Untitled',
              displayName: (docData as any).displayName || (docData as any).title || docData.fileName,
              documentType: docData.documentType,
              nextReviewDate: docData.nextReviewDate as any,
              lastReviewDate: docData.lastReviewDate as any,
            });
          }
        } catch (err) {
          console.error(`[AuditLinkedDocuments] Error fetching document ${docId}:`, err);
        }
      }

      setDocuments(fetchedDocs);
    } catch (error) {
      console.error('[AuditLinkedDocuments] Error fetching linked documents:', error);
      toast.error('Failed to load linked documents');
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id, linkedDocuments]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleAddDocument = () => {
    const today = new Date();
    const newPrefillData = {
      documentType: defaultDocumentType,
      status: 'Draft',
      title: auditScope ? `Audit: ${auditScope}` : `Audit Document - ${auditId}`,
      description: auditScope
        ? `Supporting document for audit: ${auditScope}`
        : `Supporting document for audit ${auditId}`,
      dateAdded: renderDate(today),
      lastReviewDate: today.toISOString(),
      nextReviewDate: new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()).toISOString(),
      linkedAudits: [auditId],
    };

    setPrefillData(newPrefillData);
    setDialogKey(prev => prev + 1);
    setIsDocumentDialogOpen(true);
  };

  const handleDocumentSave = async (savedDoc: any) => {
    toast.success('Document saved successfully');
    setIsDocumentDialogOpen(false);

    if (savedDoc?.docID && onDocumentsChange) {
      const newLinkedDocs = [...linkedDocuments, savedDoc.docID];
      await onDocumentsChange(newLinkedDocs);
      await fetchDocuments(newLinkedDocs);
    } else {
      await fetchDocuments();
    }
  };

  const handleUnlinkDocument = async (docId: string) => {
    if (!onDocumentsChange) return;

    try {
      const newLinkedDocs = linkedDocuments.filter(id => id !== docId);
      await onDocumentsChange(newLinkedDocs);
      toast.success('Document unlinked');
      setShowUnlinkDialog(false);
      setDocToUnlink(null);
      await fetchDocuments(newLinkedDocs);
    } catch (error) {
      console.error('[AuditLinkedDocuments] Error unlinking document:', error);
      toast.error('Failed to unlink document');
    }
  };

  return (
    <>
      <Card className="mt-6">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CardHeader className="pb-3">
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Linked Documents
                  <Badge variant="secondary" className="ml-2">
                    {documents.length}
                  </Badge>
                </CardTitle>
                <div className="flex items-center gap-2">
                  {!isReadOnly && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          setShowLinkDialog(true);
                        }}
                      >
                        <LinkIcon className="h-4 w-4 mr-1" />
                        Link Existing
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          handleAddDocument();
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Create New
                      </Button>
                    </>
                  )}
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading documents...</span>
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-10 w-10 text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground">No linked documents</p>
                  {!isReadOnly && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Click "Create New" to add a supporting document for this audit
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map(doc => (
                    <div
                      key={doc.docID}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText className="h-5 w-5 text-primary shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">
                            {doc.displayName || doc.fileName}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {doc.documentType && (
                              <Badge variant="outline" className="text-xs">
                                {doc.documentType}
                              </Badge>
                            )}
                            {doc.nextReviewDate && (
                              <span>
                                Next review: {renderDate(doc.nextReviewDate)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/documentdetail/${doc.docID}`, '_blank')}
                          title="Open in new tab"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        {!isReadOnly && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDocToUnlink(doc.docID);
                              setShowUnlinkDialog(true);
                            }}
                            title="Unlink document"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      <Dialog open={isDocumentDialogOpen} onOpenChange={setIsDocumentDialogOpen}>
        <DialogContent
          className="w-screen h-screen max-w-none p-0 m-0 rounded-none bg-card/40"
          style={{
            width: '100vw',
            maxWidth: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          <div className="h-full">
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-auto p-4">
                <DialogDocumentDetail
                  key={dialogKey}
                  prefillData={prefillData}
                  onSave={handleDocumentSave}
                  onClose={() => setIsDocumentDialogOpen(false)}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showUnlinkDialog} onOpenChange={setShowUnlinkDialog}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Unlink Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to unlink this document from the audit? The document will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnlinkDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => docToUnlink && handleUnlinkDocument(docToUnlink)}
            >
              Unlink
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showLinkDialog}
        onOpenChange={open => {
          setShowLinkDialog(open);
          if (!open) setSelectedDocsToLink([]);
        }}
      >
        <DialogContent className="sm:max-w-[500px] bg-card">
          <DialogHeader>
            <DialogTitle>Link Existing Documents</DialogTitle>
            <DialogDescription>
              Select documents from your document library to link to this audit.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <DocumentsProvider>
              <DocumentsCombobox
                selected={selectedDocsToLink}
                setSelected={setSelectedDocsToLink}
                excludeIds={linkedDocuments}
                placeholder="Search and select documents..."
                fullWidth
              />
            </DocumentsProvider>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowLinkDialog(false);
                setSelectedDocsToLink([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (selectedDocsToLink.length > 0 && onDocumentsChange) {
                  const newLinkedDocs = [...linkedDocuments, ...selectedDocsToLink];
                  await onDocumentsChange(newLinkedDocs);
                  await fetchDocuments(newLinkedDocs);
                  toast.success(`Linked ${selectedDocsToLink.length} document(s)`);
                  setShowLinkDialog(false);
                  setSelectedDocsToLink([]);
                }
              }}
              disabled={selectedDocsToLink.length === 0}
            >
              Link {selectedDocsToLink.length > 0 ? `(${selectedDocsToLink.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuditLinkedDocuments;
